import { ReactNode, forwardRef, useMemo, useState } from 'react';

import { RiArrowDownSLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  CheckIcon,
  Combobox,
  Group,
  Pill,
  PillsInput,
  useCombobox,
  ComboboxItem,
  ComboboxProps,
  PillsInputProps,
  ScrollArea
} from '@mantine/core';

import { InputLoader } from '../../feedback';

import { MultiSelectOverrideClassnames } from './MultiSelect.styles';

type ModifiedPillsInputProps = Omit<ComboboxProps & PillsInputProps, 'onChange'>;

interface MultiSelectProps extends ModifiedPillsInputProps {
  name?: string;
  loading?: boolean;
  /**
   * Array of options for the multiselect dropdown
   */
  data?: ComboboxItem[];
  /**
   * Array of options for the multiselect dropdown (alternative to data)
   */
  options?: ComboboxItem[];
  /**
   * To hide the selected options from the dropdown
   */
  hideActiveOptions?: boolean;
  /**
   * The maximum number of pills to be shown after which +n values will be shown.
   */
  maxDisplayedValues?: number;
  displayedValuesLabel?: string;
  searchable?: boolean;
  clearable?: boolean;
  value?: string[];
  readOnly?: boolean;
  onChange?: (val: string[]) => void;
  checkIconPosition?: 'left' | 'right';
  placeholder?: string;
  withinPortal?: boolean;
  scrollContainerHeight?: number;
  scrollContainerType?: 'auto' | 'always' | 'scroll' | 'hover' | 'never';
}

const MultiSelect = forwardRef<HTMLInputElement, MultiSelectProps>(
  (
    {
      size = 'md',
      rightSection,
      loading,
      error,
      data,
      options,
      onFocus,
      onBlur,
      placeholder,
      hideActiveOptions = false,
      maxDisplayedValues = 2,
      searchable = false,
      clearable = false,
      value,
      readOnly = false,
      disabled = false,
      onChange,
      radius = 'xs',
      checkIconPosition = 'left',
      withinPortal = false,
      scrollContainerType = 'auto',
      displayedValuesLabel,
      scrollContainerHeight,
      rightSectionPointerEvents,
      ...rest
    },
    ref
  ) => {
    // Use options if provided, otherwise fall back to data for backward compatibility
    const items = options || data || [];

    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption(),
      onDropdownOpen: () => combobox.updateSelectedOptionIndex('active')
    });

    const [search, setSearch] = useState<string>('');
    const [localValue, setLocalValue] = useState<string[]>(
      Array.isArray(value) ? value : value ? [value] : []
    );

    // update localValue whenever value prop changes
    useMemo(() => {
      setLocalValue(Array.isArray(value) ? value : value ? [value] : []);
    }, [value]);

    const handleValueSelect = (val: string) => {
      let selectedValues: string[] = [];
      setLocalValue((current) => {
        selectedValues = current.includes(val)
          ? current.filter((v) => v !== val)
          : [...current, val];

        return selectedValues;
      });

      onChange?.(selectedValues);

      setSearch('');
    };

    const handleValueRemove = (val: string) => {
      let selectedValues: string[] = [];
      setLocalValue((current) => {
        selectedValues = current.filter((v) => v !== val);
        return selectedValues;
      });

      onChange?.(selectedValues);
    };

    const [focussed, setFocussed] = useState<boolean>(false);

    const clearButton = clearable && !!value && !disabled && !readOnly && (
      <Combobox.ClearButton
        size={size as string}
        // {...clearButtonProps}
        onClear={() => {
          setLocalValue([]);
          onChange?.([]);
        }}
      />
    );

    let rightSectionFinal: ReactNode | undefined = undefined;
    let enableRightSectionEvents = true;

    if (loading) {
      rightSectionFinal = <InputLoader size={size} />;
      enableRightSectionEvents = false;
    } else if (error) {
      rightSectionFinal = <RiErrorWarningLine />;
      enableRightSectionEvents = false;
    } else if (clearable) {
      if (value && value.length) {
        rightSectionFinal = clearButton;
        enableRightSectionEvents = true;
      } else {
        rightSectionFinal = <RiArrowDownSLine />;
        enableRightSectionEvents = false;
      }
    } else if (rightSection) {
      rightSectionFinal = rightSection;
      enableRightSectionEvents = false;
    } else {
      rightSectionFinal = <RiArrowDownSLine />;
      enableRightSectionEvents = false;
    }

    const classes = useMemo(() => {
      return MultiSelectOverrideClassnames({
        size,
        focussed,
        error,
        rightSection: rightSectionFinal,
        checkIconPosition,
        enableRightSectionEvents
      });
    }, [
      size,
      focussed,
      error,
      rightSectionFinal,
      enableRightSectionEvents,
      checkIconPosition
    ]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(false);
      onBlur && onBlur(e);
    };

    //To hide the active options if hideActiveOptions=true
    const filteredOptions = hideActiveOptions
      ? items.filter((item: ComboboxItem) => !localValue.includes(item.value))
      : items;

    //To filter the options by search keyword if searchable=true
    const finalOptions = searchable
      ? filteredOptions.filter((item: ComboboxItem) =>
          item.label.toLowerCase().includes(search.trim().toLowerCase())
        )
      : filteredOptions;

    const renderedOptions = finalOptions.map((item: ComboboxItem) => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        disabled={item.disabled}
        active={localValue.includes(item.value)}
        className={classes.option}
      >
        <Group gap="sm" className={classes.tickRight}>
          {checkIconPosition === 'left' ? (
            <>
              {localValue.includes(item.value) ? (
                <CheckIcon size={12} className={classes.icon} />
              ) : null}
              <span>{item.label}</span>
            </>
          ) : (
            <>
              <span>{item.label}</span>
              {localValue.includes(item.value) ? (
                <CheckIcon size={12} className={classes.icon} />
              ) : null}
            </>
          )}
        </Group>
      </Combobox.Option>
    ));

    const values = localValue
      .slice(
        0,
        maxDisplayedValues === localValue.length
          ? maxDisplayedValues
          : maxDisplayedValues - 1
      )
      .map((item: string) => {
        const label =
          items.find((el: ComboboxItem) => item === el.value)?.label ?? '';
        const displayLabel = label.length > 30 ? `${label.slice(0, 30)}…` : label;

        return (
          <Pill
            classNames={{
              root: classes.pill,
              remove: classes.remove
            }}
            key={item}
            withRemoveButton={!readOnly}
            onRemove={() => handleValueRemove(item)}
          >
            {displayLabel}
          </Pill>
        );
      });

    return (
      <Combobox
        store={combobox}
        onOptionSubmit={handleValueSelect}
        withinPortal={withinPortal}
      >
        <Combobox.DropdownTarget>
          <PillsInput
            {...rest}
            ref={ref}
            disabled={disabled}
            size={size}
            rightSection={rightSectionFinal}
            error={error}
            onFocus={handleFocus}
            onBlur={handleBlur}
            radius={radius}
            pointer
            onClick={() => combobox.toggleDropdown()}
            classNames={{
              wrapper: classes.wrapper,
              input: classes.input,
              section: classes.section,
              label: classes.label,
              error: classes.error
            }}
            rightSectionPointerEvents={
              rightSectionPointerEvents ||
              (enableRightSectionEvents ? 'all' : 'none') ||
              (clearButton ? 'all' : 'none')
            }
          >
            <Pill.Group className={classes.pillsList}>
              {localValue.length > 0 ? (
                localValue.length > maxDisplayedValues ? (
                  <Pill className={classes.pill}>
                    {localValue.length}{' '}
                    {displayedValuesLabel ? displayedValuesLabel : 'items'}
                  </Pill>
                ) : (
                  <>
                    {values}
                    {localValue.length > maxDisplayedValues && (
                      <Pill className={classes.pill}>
                        +{localValue.length - (maxDisplayedValues - 1)} more
                      </Pill>
                    )}
                  </>
                )
              ) : null}

              <Combobox.EventsTarget>
                <PillsInput.Field
                  placeholder={localValue?.length ? '' : placeholder}
                  value={search}
                  type={
                    searchable
                      ? 'visible'
                      : localValue?.length
                      ? 'hidden'
                      : 'visible'
                  }
                  className={classes.inputField}
                  readOnly={!searchable}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    combobox.openDropdown();
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                  }}
                  onBlur={(event) => {
                    setSearch('');
                    handleBlur(event);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Backspace' && search.length === 0) {
                      event.preventDefault();
                      handleValueRemove(localValue[localValue.length - 1]);
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>

        {!readOnly && !disabled && (
          <Combobox.Dropdown className={classes.dropdown}>
            <Combobox.Options className={classes.options}>
              <ScrollArea.Autosize
                mah={scrollContainerHeight || 300}
                type={scrollContainerType}
              >
                {renderedOptions.length === 0 ? (
                  <Combobox.Empty>
                    {hideActiveOptions ? 'All options selected' : 'Nothing found'}
                  </Combobox.Empty>
                ) : (
                  renderedOptions
                )}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        )}
      </Combobox>
    );
  }
);

export { MultiSelect };
export type { MultiSelectProps };
